import Chart from 'chart.js';
import { draw } from 'patternomaly'
import { event } from '../../../Foundation';

export default class LoyaltyDashboardReport {
    /**
    * 
    * @param {HTMLElement} element Video DOM element
    */
    constructor(element, opts) {
        // Name, reference
        element.LoyaltyDashboardReport = this;

        // # Data structure, references eg.
        this._refs = {
            'el': element,
            'chart': {
                'wrapper': element.querySelector('.loyalty-dashboard__chart-wrapper'),
                'control': {
                    'el': element.querySelector('.loyalty-dashboard-report__controls')
                }
            }, 
            'lineItems': Array.from(element.querySelectorAll('.datatable__item-toggl')),
            'converter': {
                'el': element.querySelector('.loyalty-dashboard-report__controls.justify-end')
            }
        };

        this._configs = [];
        this._opts = opts;

        // # Initialization
        if(this._refs.chart.wrapper != null) {
            this._refs.chart.canvas = this._refs.chart.wrapper.querySelector('canvas');
            if(this._refs.chart.canvas != null) {
                // Find control items
                this._refs.chart.control.items = Array.from(this._refs.chart.wrapper.querySelectorAll('.loyalty-dashboard-report__controls-item'));

                // Initialize chart handling
                this._onChartInit();    

                if(this._refs.converter.el != null) {
                    this._refs.converter.items = Array.from(this._refs.converter.el.querySelectorAll('.loyalty-dashboard-report__controls-item'));
                    this._refs.converter.items.forEach(item => item.firstElementChild.addEventListener('click', this._onConverterItemClick.bind(this)));
                }
            }
        }

        this._refs.lineItems.forEach(item => item.addEventListener('click', this._onLineItemToggl.bind(this)));
    }

    /**
     * Initalize chart for a report-section
     */
    _onChartInit () {
        if(this._refs.chart.control.el == null) {
            return false;
        }

        // Generate configurations
        const oModel = this._getChartModel();
        
        // Graph
        this._graph = new Chart(this._refs.chart.canvas, { ...this._chartConfig, ...oModel });
        
        // Control
        this._refs.chart.control.items.forEach(item => item.firstElementChild.addEventListener('click', this._onControlItemClick.bind(this)));
    }
 
    /**
     * Get default configuration for a chart based on preselected control item
     * @returns {Object}
     */
    _getChartModel (index = 1) {
        let
            zeroValuesSettings = [],
            arrTableRows = Array.from(this._refs.el.querySelectorAll('.datatable__body .datatable__item .datatable__item-header')),
            arrTotalValues = Array.from(this._refs.el.querySelectorAll('.datatable__footer .datatable__cell')),
            oModel = {
                'data': {
                    'total': arrTotalValues[index].innerText.trim(),
                    'labels': [],
                    'datasets': []
                }
            },
            oData = {
                'label': 'Dataset ' + index,
                'data': [],
                'backgroundColor': []
            };

        arrTableRows.filter(x => {
            const oColor = x.querySelector('.loyalty-dashboard__report-color');
            return oColor || false;
        }).forEach(row => {
            const 
                oLabel = row.children[0],
                oValue = row.children[index];

            if(oLabel != null) {
                const oLabelColor = oLabel.children[0];
                let backgroundColor = 'rgb(37, 55, 70)';
                if(oLabelColor != null) {
                    backgroundColor = oLabelColor.style.backgroundColor;
                }

                // Chart settings
                let chartContentSettings = {
                    backgroundColor: backgroundColor,
                    percentage: 100,
                    label: ''
                };
                
                // Chart value
                if(oValue != null) {
                    const fieldValue = oValue.innerText.trim().replace(',','');
                    const fieldLabel = oLabel.innerText.trim() + ':' + oValue.innerText.trim();
                    chartContentSettings.oValue = oValue;
                    chartContentSettings.label = fieldLabel;
                    // Calc percentage on each columns
                    const percentage = (this._stripUnit(fieldValue) / this._stripUnit(arrTotalValues[index].innerText.trim())) * 100;
                    // If percentage is not 0 / NaN
                    if(percentage) {
                        // Set chart percentage
                        chartContentSettings.percentage = percentage;
                        /** 
                         * Show negative chart if percentage is less than 0
                         */
                        if(this._stripUnit(fieldValue) < 0) {
                            chartContentSettings = this._showNegativeChart(chartContentSettings);
                        }
                        // Labels on hover over chart
                        oModel.data.labels.push(fieldLabel);
                        // Add Chart value
                        oData.data.push(chartContentSettings.percentage);
                        // Add Chart color
                        oData.backgroundColor.push(chartContentSettings.backgroundColor);
                    }
                }

                // Save colors and labes for zero Value data
                zeroValuesSettings.push(chartContentSettings);
            }
        });

        /**
         * If all data values are 0
         */
        const checkDataValues = oData.data.filter(num => num);
        if(checkDataValues.length === 0) {
            // Empty display default chart if No label AND No Value
            if(zeroValuesSettings.length === 0) {
                let defaultChartContentSetting = {
                    backgroundColor: draw('diagonal', 'rgba(37, 55, 70, 0.5)'),
                    percentage: 100
                }
                zeroValuesSettings.push(defaultChartContentSetting);
            }

            // Show all zero values labels + colors
            for(const setting of zeroValuesSettings) {
                let zeroVal = setting;
                if(setting.oValue != null) {
                    zeroVal = this._showNegativeChart(setting);
                }
                oModel.data.labels.push(zeroVal.label);
                oData.data.push(zeroVal.percentage);
                oData.backgroundColor.push(zeroVal.backgroundColor)
            }
        }

        // Merge data-model into main-model
        oModel.data.datasets.push(oData);

        return oModel;
    }

    /** 
     * Set default Chart setting when content data is 0 
    */
    _showNegativeChart(oContent) {
        const bgColor = oContent.backgroundColor.replace('rgb', 'rgba').replace(')', ', 0.5)');
        oContent.backgroundColor = draw('diagonal', bgColor);
        return oContent;
    }

    /**
     * Handler for swapping between different calculation methods on a table
     * @param {Event} e - Event interface
     */
    _onConverterItemClick (e) {
        const 
            oItem = e.target,
            oItemParent = oItem.parentElement;

        e.preventDefault();

        if(oItemParent.classList.contains('is-active')) return false;
  
        // Active state
        this._refs.converter.items.forEach(x => x.classList.remove('is-active'));

        oItemParent.classList.add('is-active');

        // Swap values
        const arrSwappableValues = Array.from(this._refs.el.querySelectorAll('[data-swappable-value]'));
        arrSwappableValues.forEach(el => {
            const value = el.dataset.swappableValue, text = el.innerText;

            el.innerText = value;
            el.setAttribute('data-swappable-value', text);
        });

        // Update chart
        const oSelectedControlItem = this._refs.chart.control.items.filter(x => x.classList.contains('is-active'));
        this._updateChartData(Array.from(oSelectedControlItem[0].parentNode.children).indexOf(oSelectedControlItem[0]) + 1);
    }

    /**
     * Initialize and build data for graph
     * @param {Event} e - Event interface
     */ 
    _onControlItemClick (e) {
        e.preventDefault();

        const oItem = e.target, oItemParent = oItem.parentElement;

        if(oItemParent.classList.contains('is-active')) {
            return false;
        }

        // Reset active-state
        this._refs.chart.control.items.forEach(item => item.classList.remove('is-active'));

        // Set active-state
        oItemParent.classList.add('is-active');

        this._updateChartData(Array.from(oItemParent.parentNode.children).indexOf(oItemParent) + 1);
    }

    /**
     * Update dataset for Chart and re-render
     * @param {Integer} Index - Which index should be used
     */
    _updateChartData (index = null) {
        const oData = this._getChartModel(index);

        this._graph.data.labels = oData.data.labels;
        this._graph.data.datasets.pop();

        oData.data.datasets.forEach(dataset => this._graph.data.datasets.push(dataset));

        // Update chart
        this._graph.update(); 

        // Update total value
        const oTotalValue = this._refs.chart.wrapper.querySelector('h3');
        if(oTotalValue != null) {
            oTotalValue.innerHTML = oData.data.total;
        }
    }

    /**
     * Enables toggling of item line content
     * @param {Event} e - Event Interface
     */ 
    _onLineItemToggl(e) {
        e.preventDefault();

        const
            oLink = e.target.parentElement,
            oItem = oLink.parentElement.parentElement.parentElement,
            oItemFoldout = oItem.children[oItem.children.length - 1],
            oItemFoldoutInner = oItemFoldout.children[0].offsetHeight,
            // classes
            strClassVisible = 'is-visible',
            strClassActive = 'is-active',
            // functions
            fnTransitionEnd = (e) => {
                const oItem = e.target;
                if(oItem.classList.contains(strClassVisible)) {
                    oItem.style.overflow = 'visible';
                } 
                oItemFoldout.removeEventListener('transitionend', fnTransitionEnd);
            };

        if (oItemFoldout.classList.contains(strClassVisible)) {
            oItemFoldout.style.maxHeight = 0;

            event.emit(this._opts.initiator, 'loyalty.dashboard.update', {
                'height': oItemFoldoutInner,
                'type': 'subtraction'
            });

            oLink.classList.remove(strClassActive);

            oItemFoldout.classList.remove(strClassVisible);
            oItemFoldout.style.overflow = 'hidden';
        } else {
            oItemFoldout.style.maxHeight = oItemFoldoutInner + 'px';

            event.emit(this._opts.initiator, 'loyalty.dashboard.update', {
                'height': oItemFoldoutInner,
                'type': 'addition'
            });

            oLink.classList.add(strClassActive);

            oItemFoldout.classList.add(strClassVisible); 
            oItemFoldout.addEventListener('transitionend', fnTransitionEnd);
        }
    }

    /**
     * Strip unit and unessecary
     * @param {String} value 
     * @returns {Number}
     */
    _stripUnit (value) {
        return Number(value.replace(/[^-|^0-9\.]+/g,""));
    };

    /**
     * Base configuration for chart within reports-section
     * @returns {Object}
     */
    get _chartConfig() {
        return {
            type: 'doughnut',
            options: {
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: this._tooltip
                },
                cutout: '80%'
            }
        };
    }

    /**
     * Base configuration for custom chart tooltip
     * @returns {Object}
     */
    get _tooltip () {
        return { 
            // Disable the on-canvas tooltip
             enabled: false,

             external: function(context) {
                 // Tooltip Element
                 let tooltipEl = document.getElementById('chartjs-tooltip');

                 // Create element on first render
                 if (!tooltipEl) {
                     tooltipEl = document.createElement('div');
                     tooltipEl.id = 'chartjs-tooltip';
                     tooltipEl.innerHTML = '<table></table>';
                     document.body.appendChild(tooltipEl);
                 }

                 // Hide if no tooltip
                 var tooltipModel = context.tooltip;
                 if (tooltipModel.opacity === 0) {
                     tooltipEl.style.opacity = 0;
                     return;
                 }

                 // Set caret Position
                 tooltipEl.classList.remove('above', 'below', 'no-transform');
                 if (tooltipModel.yAlign) {
                     tooltipEl.classList.add(tooltipModel.yAlign);
                 } else {
                     tooltipEl.classList.add('no-transform');
                 }

                 function getBody(bodyItem) {
                     return bodyItem.lines;
                 }

                 // Set Text
                 if (tooltipModel.body) {
                     const 
                         titleLines = tooltipModel.title || [],
                         bodyLines = tooltipModel.body.map(getBody);
                     
                     let innerHtml = '<thead>';

                     titleLines.forEach(function(title) {
                         innerHtml += '<tr><th>' + title + '</th></tr>';
                     });
                     innerHtml += '</thead><tbody>';

                     bodyLines.forEach(function(body, i) {
                         
                         const strBody = body[i].split(':');
                         innerHtml += '<tr><td><span>' + strBody[0] + '</span>'+ strBody[1] +'</td></tr>';
                     });
                     innerHtml += '</tbody>';

                     const tableRoot = tooltipEl.querySelector('table');
                     tableRoot.innerHTML = innerHtml;
                 }

                 const position = context.chart.canvas.getBoundingClientRect();

                 // Display, position, and set styles for font
                 tooltipEl.style.opacity = 1;
                 tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
                 tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                 
                 tooltipEl.style.padding = tooltipModel.padding + 'px ' + tooltipModel.padding + 'px';
                 tooltipEl.style.pointerEvents = 'none';
             }
         }
    }
}
